import { useCallback, useEffect, useMemo, useState } from "react";
import { CloseOutlined } from "@material-ui/icons";
import "./CGFAIStudioModal.css";
import { getWorksheets } from "./services/worksheet.services";
import { getLessonPlans } from "./services/lessonplan.services";
import { StatusView } from "./components/StatusView";
import { Report } from "./Report";

const tabsConfig = [
  { label: "Content Creation Tool", value: "content" },
  { label: "Answer Sheets Evaluation Tool", value: "evaluation" },
  { label: "Reports", value:"reports"}
];

const Tabs = ({ tabs, activeTab, setActiveTab }) => {
  return (
    <div className="ai-tabs">
      {tabs.map((tab) => (
        <button
          key={tab.value}
          className={`ai-tab ${activeTab === tab.value ? "active" : ""}`}
          onClick={() => setActiveTab(tab.value)}
        >
          {tab.label}
        </button>
      ))}
    </div>
  );
};

const AIStudioModal = ({
  loading,
  error,
  token,
  query,
  handleClose,
  handleRetry,
}) => {
  const [activeTab, setActiveTab] = useState("content");
  const [worksheetList, setWorksheetList] = useState({
    loading: false,
    error: false,
    message: "",
    list: [],
  });

  const [lessonPlanList, setLessonPlanList] = useState({
    loading: false,
    error: false,
    message: "",
    list: [],
  });

  const { CONTENT_URL, EVALUATION_URL } = useMemo(() => {
    const baseContentUrl = process.env.REACT_APP_CGF_CONTENT_TOOL_URL;
    const baseEvaluationUrl = process.env.REACT_APP_CGF_EVALUATION_TOOL_URL;

    // If no token OR no query → return base URLs
    if (!token || !query) {
      return {
        CONTENT_URL: baseContentUrl,
        EVALUATION_URL: baseEvaluationUrl,
      };
    }

    const queryString = new URLSearchParams({
      token,
      ...query,
    }).toString();

    return {
      CONTENT_URL: `${baseContentUrl}?${queryString}`,
      EVALUATION_URL: `${baseEvaluationUrl}?${queryString}`,
    };
  }, [token, query]);

  const fetchLessonPlans = useCallback(async (token, queryStr) => {
    setLessonPlanList({
      loading: true,
      error: false,
      message: "Fetching lesson plans...",
      list: [],
    });

    try {
      const { data, pagination } = await getLessonPlans(token, queryStr);
      const isFound = Boolean(data?.length);
      setLessonPlanList({
        loading: false,
        error: !isFound,
        message: !isFound ? "No lesson plan created" : "",
        list: data || [],
        pagination,
      });
    } catch (error) {
      setLessonPlanList({
        loading: false,
        error: true,
        message: error?.message || "Failed to fetch lesson plans",
        list: [],
      });
    }
  }, []);

  const fetchWorksheets = useCallback(async (token, queryStr) => {
    setWorksheetList({
      loading: true,
      error: false,
      message: "Fetching worksheets...",
      list: [],
    });

    try {
      const { data, pagination } = await getWorksheets(token, queryStr);
      const isFound = Boolean(data?.length);
      setWorksheetList({
        loading: false,
        error: !isFound,
        message: !isFound ? "No worksheets created" : "",
        list: data || [],
        pagination,
      });
    } catch (error) {
      setWorksheetList({
        loading: false,
        error: true,
        message: error?.message || "Failed to fetch worksheets",
        list: [],
      });
    }
  }, []);

  function onLessonPlanPageChange(page) {
    const queryStr = new URLSearchParams({
      teacher_id: query?.teacherId,
      school_id: query?.schoolId,
      page,
    });
    fetchLessonPlans(token, queryStr);
  }

  function onWorksheetPageChange(page) {
    const queryStr = new URLSearchParams({
      teacher_id: query?.teacherId,
      school_id: query?.schoolId,
      page,
    });
    fetchWorksheets(token, queryStr);
  }

  useEffect(() => {
    if (!token) return;
    const queryStr = new URLSearchParams({ teacher_id: query?.teacherId,
      school_id: query?.schoolId, page: 1 });
    fetchLessonPlans(token, queryStr);
    fetchWorksheets(token, queryStr);
  }, [query]);

  return (
    <div className="ai-overlay">
      <div className="ai-modal">
        <StatusView
          loading={loading}
          error={error}
          message={
            error
              ? "Unable to authenticate. Please try again."
              : "Authentication"
          }
          onRetry={handleRetry}
        />

        {/* Success */}
        {!loading && !error && query && (
          <>
            <Tabs
              tabs={tabsConfig}
              activeTab={activeTab}
              setActiveTab={setActiveTab}
            />

            {/* Iframe Container */}
            <div className="ai-iframe-wrapper">
              {/* Content Tool */}
              <iframe
                title="Content Tool"
                src={CONTENT_URL}
                className={`ai-iframe ${
                  activeTab === "content" ? "visible" : "hidden"
                }`}
              />
              {/* Evaluation Tool */}
              <iframe
                title="Evaluation Tool"
                src={EVALUATION_URL}
                className={`ai-iframe ${
                  activeTab === "evaluation" ? "visible" : "hidden"
                }`}
              />
              {/**Report */}
              <Report
                lessonPlanList={lessonPlanList}
                worksheetList={worksheetList}
                onLessonPlanPageChange={onLessonPlanPageChange}
                onWorksheetPageChange={onWorksheetPageChange}
                className={activeTab === "reports"?"visible":"hidden"}
              />
            </div>
          </>
        )}
      </div>

      <button className="ai-close-btn" onClick={handleClose}>
        <CloseOutlined />
      </button>
    </div>
  );
};

export default AIStudioModal;
